import type { RouteGuardResult } from '@connected/core';
import type { QueryClient } from '@tanstack/react-query';

import { z } from 'zod';

import type { User } from '../hooks/use-authentication';
import type { Domain } from './portal';

import { me } from '../data/auth/me';
import { getAuthentication, setAuthentication, tokenSchema } from './authentication';
import { getLoginUrl } from './login';
import { getDomainValue } from './portal';

// =============================================================================
// Re-exports from @connected/core
// =============================================================================

// These types from @connected/core are the new standard for route guards.
// Import them from @connected/core directly for new code.
export {
    checkRouteGuard,
    type CheckRouteGuardParams,
    type RouteGuardConfig as CoreRouteGuardConfig,
    isPathAllowed,
    type RouteGuardResult,
} from '@connected/core';

// =============================================================================
// Legacy types (backwards compatibility)
// =============================================================================

/**
 * Context available for route guard decision callbacks
 *
 * @deprecated Use BaseConditionContext from @connected/core instead.
 * This type is kept for backwards compatibility with existing code.
 */
export type RouteGuardContext = {
    /** The currently authenticated user, or null if not authenticated */
    currentUser: User | null;
    /** Whether the user is authenticated */
    isAuthenticated: boolean;
    /** The current path being accessed */
    currentPath: string;
};

/**
 * Configuration for app-specific route restrictions (legacy API)
 *
 * @deprecated Use RouteGuardConfig from @connected/core with condition-based allowedConditions.
 * This type uses callback-based isAllowedRoute for backwards compatibility.
 *
 * @example
 * // Legacy approach (deprecated):
 * const elektraRouteGuard: LegacyRouteGuardConfig = {
 *     isAllowedRoute: (path) => isProjectPath(path) || path === '/sso' || path === '/login',
 *     getFallbackRoute: (ctx) => '/dashboard/organizations',
 *     targetDomain: 'portaal',
 * };
 *
 * // New approach (use @connected/core):
 * const elektraRouteGuard: RouteGuardConfig<ElektraConditionContext> = {
 *     allowedConditions: ['canAccessApp', 'canAccessSso', 'canAccessLogin'],
 *     getFallbackRoute: (ctx) => '/dashboard/organizations',
 *     targetDomain: 'portaal',
 * };
 */
export type LegacyRouteGuardConfig = {
    /** Determines if the current path is allowed in this app */
    isAllowedRoute: (path: string, context: RouteGuardContext) => boolean;
    /** Determines the redirect path when the route is not allowed */
    getFallbackRoute: (context: RouteGuardContext) => string;
    /** Target domain to redirect to (defaults to 'portaal') */
    targetDomain?: Domain;
};

/**
 * @deprecated Use LegacyRouteGuardConfig for the callback-based API,
 * or RouteGuardConfig from @connected/core for the new condition-based API.
 */
export type RouteGuardConfig = LegacyRouteGuardConfig;

/**
 * Schema that handles token as either a JSON string or an already-parsed object
 * TanStack Router may pass the token as a string from URL query params
 */
const tokenFromSearchSchema = z.union([
    // Already parsed object
    tokenSchema,
    // JSON string that needs parsing
    z.string().transform((str, ctx) => {
        try {
            const parsed = JSON.parse(str);
            const result = tokenSchema.safeParse(parsed);
            if (!result.success) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Invalid token structure',
                });
                return z.NEVER;
            }
            return result.data;
        }
        catch {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Invalid JSON token',
            });
            return z.NEVER;
        }
    }),
]);

/**
 * Zod schema for SSO route search parameters
 * Use this in your route's validateSearch to parse incoming SSO requests
 *
 * @example
 * export const Route = createFileRoute('/sso/')({
 *     validateSearch: ssoSearchSchema,
 *     // ...
 * });
 */
export const ssoSearchSchema = z.object({
    token: tokenFromSearchSchema.optional(),
    redirect: z.string().optional(),
});

export type SSOSearchParams = z.infer<typeof ssoSearchSchema>;

/**
 * Generate an SSO URL to redirect to another app with authentication
 * This creates a URL to the target app's /sso route with the current auth token
 *
 * @param params - SSO parameters
 * @param params.targetDomain - The target domain key (e.g., 'portaal', 'elektra-app')
 * @param params.redirectPath - Optional path to redirect to after SSO completes
 * @returns The full SSO URL with token, or null if no valid token exists
 *
 * @example
 * // Redirect user to portal dashboard
 * const url = generateSSOUrl({
 *     targetDomain: 'portaal',
 *     redirectPath: '/dashboard/organizations/123/projects',
 * });
 * if (url) {
 *     window.location.href = url;
 * }
 */
export function generateSSOUrl({
    targetDomain,
    redirectPath,
}: {
    targetDomain: Domain;
    redirectPath?: string;
}): string | null {
    const token = getAuthentication();

    if (!token) {
        console.warn('No authentication token available for SSO redirect.');
        return null;
    }

    const domainValue = getDomainValue(targetDomain);

    // Note: We don't validate against isAllowedHost here because getDomainValue
    // returns a configured domain from DOMAINS, which is already trusted.
    // The isAllowedHost check is for validating external/user-provided hosts.

    const protocol = domainValue.includes('localhost') ? 'http:' : 'https:';
    const url = new URL('/sso', `${protocol}//${domainValue}`);

    url.searchParams.set('token', JSON.stringify(token));

    if (redirectPath) {
        url.searchParams.set('redirect', redirectPath);
    }

    return url.toString();
}

/**
 * Handle an incoming SSO callback request
 * This function processes the token from the URL, sets authentication, and returns the redirect path
 *
 * @param params - SSO callback parameters
 * @param params.searchParams - The parsed search parameters from the SSO route
 * @param params.queryClient - The React Query client for invalidating cached data
 * @param params.fallbackRedirect - The path to redirect to if no redirect param is provided (default: '/')
 * @returns The path to redirect to after successful SSO
 * @throws Error if token is missing or invalid
 *
 * @example
 * // In your /sso route's beforeLoad:
 * beforeLoad: async ({ search }) => {
 *     const redirectTo = await handleSSOCallback({
 *         searchParams: search,
 *         queryClient,
 *         fallbackRedirect: '/',
 *     });
 *     throw redirect({ to: redirectTo });
 * }
 */
export async function handleSSOCallback({
    searchParams,
    queryClient,
    fallbackRedirect = '/',
}: {
    searchParams: SSOSearchParams;
    queryClient: QueryClient;
    fallbackRedirect?: string;
}): Promise<string> {
    const { token, redirect: redirectPath } = searchParams;

    if (!token) {
        throw new Error('No SSO token provided');
    }

    // Set the authentication (this validates and stores the token)
    setAuthentication(token);

    // Invalidate user data to refresh with new authentication
    // Wrap in try-catch to handle potential API errors gracefully
    try {
        await queryClient.invalidateQueries(me.queryOptions());
    }
    catch (e) {
        // Log but don't fail - the token is already set, user data will refresh on next load
        console.warn('SSO: Failed to invalidate user queries, continuing anyway:', e);
    }

    // Determine redirect path
    let targetPath = redirectPath ?? fallbackRedirect;

    // Prevent redirect loops - don't redirect back to SSO or login routes
    if (targetPath.includes('/sso') || targetPath.includes('/login')) {
        targetPath = fallbackRedirect;
    }

    return targetPath;
}

/**
 * Navigate to another app using SSO
 * Convenience function that generates the SSO URL and performs the redirect
 *
 * @param params - SSO navigation parameters
 * @param params.targetDomain - The target domain key (e.g., 'portaal', 'elektra-app')
 * @param params.redirectPath - Optional path to redirect to after SSO completes
 * @returns true if navigation was initiated, false if no valid token exists
 *
 * @example
 * // Navigate to portal dashboard
 * navigateWithSSO({
 *     targetDomain: 'portaal',
 *     redirectPath: '/dashboard',
 * });
 */
export function navigateWithSSO({
    targetDomain,
    redirectPath,
}: {
    targetDomain: Domain;
    redirectPath?: string;
}): boolean {
    const url = generateSSOUrl({ targetDomain, redirectPath });

    if (!url) {
        return false;
    }

    window.location.href = url;
    return true;
}

/**
 * Check if a route is allowed and generate the appropriate redirect URL if not (legacy API)
 *
 * @deprecated Use checkRouteGuard from @connected/core with getRedirectUrlFromResult
 * for the new condition-based system.
 *
 * @param config - The route guard configuration for this app
 * @param context - The current route guard context
 * @returns null if the route is allowed, otherwise the redirect URL (SSO URL or login URL)
 *
 * @example
 * // Legacy approach (deprecated):
 * const redirectUrl = getRouteGuardRedirect(elektraRouteGuard, {
 *     currentUser,
 *     isAuthenticated: !!currentUser,
 *     currentPath: location.pathname,
 * });
 * if (redirectUrl) {
 *     window.location.href = redirectUrl;
 *     return;
 * }
 *
 * // New approach (use @connected/core):
 * const result = checkRouteGuard({
 *     config: elektraRouteGuard,
 *     context: conditionContext,
 *     conditions: elektraConditions,
 * });
 * if (!result.allowed) {
 *     const url = getRedirectUrlFromResult(result);
 *     window.location.href = url;
 * }
 */
export function getRouteGuardRedirect(
    config: RouteGuardConfig,
    context: RouteGuardContext,
): string | null {
    // Check if the current route is allowed
    if (config.isAllowedRoute(context.currentPath, context)) {
        return null;
    }

    const targetDomain = config.targetDomain ?? 'portaal';
    const fallbackRoute = config.getFallbackRoute(context);

    // If not authenticated, redirect to login with the fallback route as the final destination
    if (!context.isAuthenticated) {
        return getLoginUrl(fallbackRoute);
    }

    // Authenticated but route not allowed - use SSO to redirect to target domain
    const ssoUrl = generateSSOUrl({
        targetDomain,
        redirectPath: fallbackRoute,
    });

    // If SSO URL generation fails (no token), fall back to login
    return ssoUrl ?? getLoginUrl(fallbackRoute);
}

// =============================================================================
// New condition-based route guard helpers
// =============================================================================

/**
 * Convert a RouteGuardResult (from checkRouteGuard) to a redirect URL.
 * This handles the SSO URL generation and login URL fallback.
 *
 * Use this with checkRouteGuard from @connected/core:
 *
 * @param result - The result from checkRouteGuard (must be a non-allowed result)
 * @returns The redirect URL (SSO URL if authenticated, login URL otherwise)
 *
 * @example
 * const result = checkRouteGuard({
 *     config: elektraRouteGuard,
 *     context: conditionContext,
 *     conditions: elektraConditions,
 * });
 *
 * if (!result.allowed) {
 *     const url = getRedirectUrlFromResult(result);
 *     window.location.href = url;
 * }
 */
export function getRedirectUrlFromResult(
    result: Extract<RouteGuardResult, { allowed: false }>,
): string {
    // If not authenticated, redirect to login with the fallback route as the final destination
    if (!result.isAuthenticated) {
        return getLoginUrl(result.fallbackRoute);
    }

    // Authenticated but route not allowed - use SSO to redirect to target domain
    const ssoUrl = generateSSOUrl({
        targetDomain: result.targetDomain as Domain,
        redirectPath: result.fallbackRoute,
    });

    // If SSO URL generation fails (no token), fall back to login
    return ssoUrl ?? getLoginUrl(result.fallbackRoute);
}
